import React, { useState, useCallback } from 'react';
import './TenantHomePage.scss';
import QuickHelpSection from 'components/tenant/QuickHelpSection';
import NotificationsSection from 'components/tenant/NotificationsSection';
import RentalUnitsCard from 'components/tenant/Cards/RentalUnitsCard';
import LeaseExpirationCard from 'components/tenant/Cards/LeaseExpirationCard';
import WarningsCard from 'components/tenant/Cards/WarningsCard';
import NextPaymentCard2 from '@/components/tenant/Cards/NextPaymentCard2';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import { TicketDictionariesResponse } from '@/types/tickets';
import UIToast from '@/components/shared/UIToast';
import { useMyLeases } from '@/hooks/useTenantLease';
import UITenancyOverview, {
  TenancyProperty,
} from '../../../components/shared/UITenancyOverwiev/UITenancyOverwiev';
import { MyLease } from '@/api/lease';

const TenantHomePage: React.FC = () => {
  const { data: dictionaries } = useGetDictionaries<TicketDictionariesResponse>('home');
  const [toastMessage, setToastMessage] = useState('');
  const { data: leases, isLoading, isError } = useMyLeases();

  const leasesToProperties = (leasesData: MyLease[]): TenancyProperty[] => {
    if (!leasesData) {
      return [];
    }
    return leasesData.map((lease) => {
      const { address } = lease.unit.property;
      const fullAddress = address
        ? `${address.street} ${address.buildingNumber}, ${address.city}`
        : 'Address not available';

      return {
        id: lease.id.toString(),
        name: lease.unit.property.name,
        type: lease.unit.property.type as 'residential' | 'commercial' | 'mixed' | undefined | null,
        address: fullAddress,
        unit: lease.unit.number,
        latitude: address?.latitude ? parseFloat(address.latitude as any) : null,
        longitude: address?.longitude ? parseFloat(address.longitude as any) : null,
        payment: {
          status: 'PENDING', // Заглушка
          rental: `$${lease.monthlyRent}`,
          debt: '$0.00', // Заглушка
          nextPayment: lease.firstPaymentDate || 'N/A',
          leaseExpiration: lease.endDate || 'N/A',
          paymentMethod: lease.paymentMethod || 'N/A',
          warnings: 0, // Заглушка
          canPayOnline: true, // Заглушка
        },
        onPropertyInfo: (propertyId: string) => console.log(`Info for ${propertyId}`),
        onContactProperty: (propertyId: string) => console.log(`Contact for ${propertyId}`),
        onReportWarning: (propertyId: string) => console.log(`Warning for ${propertyId}`),
        onMakePayment: (propertyId: string) => console.log(`Payment for ${propertyId}`),
      };
    });
  };

  const properties: TenancyProperty[] = leases ? leasesToProperties(leases) : [];

  const SupportIcon = () => (
    <svg
      className="TenantHomePage__top__info__btn__icon"
      width="28"
      height="22"
      viewBox="0 0 28 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 1.4667C10.088 1.4667 6.9167 4.7499 6.9167 8.8V10.2667H8.3333C9.1157 10.2667 9.75 10.9233 9.75 11.7333V16.1333C9.75 16.9434 9.1157 17.6 8.3333 17.6H6.9167C6.1343 17.6 5.5 16.9434 5.5 16.1333V8.8C5.5 3.9399 9.3056 0 14 0C18.6944 0 22.5 3.9399 22.5 8.8V17.6C22.5 19.625 20.9143 21.2667 18.9583 21.2667H15.9355C15.6905 21.7051 15.2327 22 14.7083 22H13.2917C12.5093 22 11.875 21.3434 11.875 20.5333C11.875 19.7233 12.5093 19.0667 13.2917 19.0667H14.7083C15.2327 19.0667 15.6905 19.3616 15.9355 19.8H18.9583C20.1319 19.8 21.0833 18.815 21.0833 17.6H19.6667C18.8843 17.6 18.25 16.9434 18.25 16.1333V11.7333C18.25 10.9233 18.8843 10.2667 19.6667 10.2667H21.0833V8.8C21.0833 4.7499 17.912 1.4667 14 1.4667Z"
        fill="#105E96"
      />
    </svg>
  );

  const handleTicketCreated = useCallback(() => {
    setToastMessage('Ticket created successfully! You can see it on the tickets page.');
  }, []);

  const renderTenancyOverview = () => {
    if (isLoading) {
      return <div className="TenancyOverviewSection-loading">Loading...</div>;
    }

    if (isError) {
      return <div className="TenancyOverviewSection-error">Error fetching leases.</div>;
    }

    return properties.length > 0 ? (
      <UITenancyOverview title="Tenancy Overview" properties={properties} />
    ) : (
      <p>You have no leases.</p>
    );
  };

  return (
    <div className="TenantHomePage">
      <div className="TenantHomePage__top">
        <div className="TenantHomePage__top__info">
          <h1 className="TenantHomePage__top__info__title">Dashboard / Tenant</h1>
          <a href="#" className="TenantHomePage__top__info__btn">
            <SupportIcon />
            <span className="TenantHomePage__top__info__btn__text">Support</span>
          </a>
        </div>
      </div>

      <div className="TenantHomePage__cards">
        <RentalUnitsCard />
        <NextPaymentCard2 />
        <LeaseExpirationCard />
        <WarningsCard />
      </div>

      <div className="TenantHomePage__content">
        <QuickHelpSection
          categories={dictionaries?.ticketCategories || []}
          onTicketCreated={handleTicketCreated}
          dictionarySource="home"
        />

        {renderTenancyOverview()}

        <NotificationsSection />
      </div>
      {toastMessage && <UIToast message={toastMessage} onClose={() => setToastMessage('')} />}
    </div>
  );
};

export default TenantHomePage;
